import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Rocket, Building2, Sparkles, ShieldCheck, UserPlus,
  ChevronLeft, ChevronRight, Check,
} from "lucide-react";
import logger from "@/lib/logger";

/**
 * OnboardingWizard — one-time 4-step wizard for fresh Admins.
 *
 * Auto-opens on Dashboard mount when GET /api/onboarding/status returns
 * `should_open: true`. Also mounts a bookmark trigger in Admin →
 * Workspace Settings via the same component (`triggerOpen` prop) so an
 * Admin who dismissed too fast can walk through it again.
 *
 * Non-Admin roles never see this wizard.
 */
const STEPS = [
  { key: "welcome",  icon: Rocket,       title: "Welcome to VitaQMS" },
  { key: "workspace", icon: Building2,   title: "Workspace identity" },
  { key: "portfolio", icon: Sparkles,    title: "What do you make?" },
  { key: "signatory", icon: ShieldCheck, title: "Audit signatory" },
  { key: "invite",    icon: UserPlus,    title: "Invite your first teammate" },
];

export default function OnboardingWizard({ triggerOpen = false, onClosed }) {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("Admin");
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    org_name: "",
    signatory_name: "",
    signatory_title: "Head of QA",
    signatory_email: "",
    device_portfolio_keywords: "",
    invite_email: "",
    invite_name: "",
    invite_role: "QA Lead",
  });

  // Load status on mount + whenever an external trigger fires.
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/onboarding/status");
        setStatus(data);
        // Prefill from any partial existing settings so we never overwrite
        // work the admin already did before starting the wizard.
        setForm((f) => ({
          ...f,
          org_name: data.workspace?.org_name || f.org_name,
          signatory_name: data.workspace?.signatory_name || f.signatory_name,
          signatory_title: data.workspace?.signatory_title || f.signatory_title,
          signatory_email: data.workspace?.signatory_email || f.signatory_email,
          device_portfolio_keywords:
            data.workspace?.device_portfolio_keywords || f.device_portfolio_keywords,
        }));
        if (data.should_open) setOpen(true);
      } catch (e) {
        logger.warn("onboarding status:", e?.response?.status);
      }
    })();
  }, [isAdmin]);

  // Manual trigger (from Admin settings "Re-open onboarding").
  useEffect(() => {
    if (triggerOpen && isAdmin) {
      // Reset the server-side flag so the auto-open logic wouldn't fight us.
      api.post("/onboarding/reset").catch(() => {});
      setStep(0);
      setOpen(true);
    }
  }, [triggerOpen, isAdmin]);

  const total = STEPS.length;
  const StepIcon = STEPS[step].icon;

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return true;
    if (step === 1) return form.org_name.trim().length >= 2;
    if (step === 2) return true; // portfolio keywords optional
    if (step === 3) return form.signatory_name.trim().length >= 2 && form.signatory_email.includes("@");
    if (step === 4) return true; // invite optional
    return true;
  };

  const persistWorkspace = async () => {
    // We push whatever the wizard has captured so far — the endpoint is
    // a full replace but we prefilled with existing values so nothing else
    // gets stomped.
    await api.put("/workspace-settings", {
      org_name: form.org_name.trim(),
      logo_url: status?.workspace?.logo_url || null,
      signatory_name: form.signatory_name.trim() || null,
      signatory_title: form.signatory_title.trim() || null,
      signatory_email: form.signatory_email.trim() || null,
      device_portfolio_keywords: form.device_portfolio_keywords.trim() || null,
    });
  };

  const submitInvite = async () => {
    // Fire-and-forget style — a failure here shouldn't block "Finish".
    if (!form.invite_email.trim()) return false;
    try {
      await api.post("/admin/invites", {
        email: form.invite_email.trim(),
        name: form.invite_name.trim() || null,
        roles: [form.invite_role],
        expires_in_hours: 72,
      });
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed — you can still finish and try again from /admin");
      return false;
    }
  };

  const finish = async (skipped = false) => {
    setSaving(true);
    let inviteSent = false;
    try {
      if (!skipped) {
        await persistWorkspace();
        inviteSent = await submitInvite();
      }
      await api.post("/onboarding/complete", { skipped });
      toast.success(skipped
        ? "You can re-open the wizard anytime from Admin → Workspace Settings."
        : inviteSent
          ? "Workspace ready — first invite sent."
          : "Workspace ready."
      );
      setOpen(false);
      onClosed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't finish onboarding — please try again");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg" data-testid="onboarding-wizard">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-vq-blue/10 text-vq-blue">
              <StepIcon className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg" data-testid="onboarding-step-title">
                {STEPS[step].title}
              </DialogTitle>
              <DialogDescription className="text-xs mt-0.5">
                Step {step + 1} of {total} · under 5 minutes
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Progress bar */}
        <div className="flex items-center gap-1 mt-2 mb-4" data-testid="onboarding-progress">
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              className={`h-1.5 flex-1 rounded-full transition-all ${
                i <= step ? "bg-vq-blue" : "bg-slate-200"
              }`}
              data-testid={`onboarding-progress-${i}`}
            />
          ))}
        </div>

        {/* Step content */}
        {step === 0 && (
          <div className="space-y-3 text-sm text-slate-600" data-testid="onboarding-step-welcome">
            <p>
              We&apos;ll take three minutes to name your workspace, tell VitaQMS what
              you make, set up your audit signatory, and invite your first
              teammate. You can skip anytime and finish later from Admin
              settings.
            </p>
            <ul className="text-xs space-y-1.5 text-slate-500">
              <li>• Your organisation&apos;s name (appears on every signed export)</li>
              <li>• A one-line description of your device portfolio (AI triage uses this)</li>
              <li>• Who signs off exports and receives signed PDFs</li>
              <li>• One optional invite to your QA lead / RA colleague</li>
            </ul>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3" data-testid="onboarding-step-workspace">
            <Label htmlFor="onb-orgname">Organisation name</Label>
            <Input
              id="onb-orgname"
              data-testid="onboarding-org-name"
              placeholder="e.g. Acme Medical Devices Ltd."
              value={form.org_name}
              onChange={(e) => patch("org_name", e.target.value)}
              autoFocus
            />
            <p className="text-xs text-slate-500">
              This appears on every signed export, digest PDF, and audit binder.
            </p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="onboarding-step-portfolio">
            <Label htmlFor="onb-portfolio">
              Device portfolio keywords <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Textarea
              id="onb-portfolio"
              data-testid="onboarding-portfolio"
              rows={4}
              placeholder="e.g. Continuous glucose monitors, insulin pumps, Class IIb software as medical device (SaMD), ISO 14971 risk management, hospital-based use in EU + US markets."
              value={form.device_portfolio_keywords}
              onChange={(e) => patch("device_portfolio_keywords", e.target.value)}
            />
            <p className="text-xs text-slate-500">
              The regulatory alerts AI uses these keywords to score every incoming
              FDA/EMA/CDSCO update against your portfolio. Skip if you&apos;re not sure —
              you can add this later from Admin settings.
            </p>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="onboarding-step-signatory">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="onb-signer-name">Signatory name</Label>
                <Input
                  id="onb-signer-name"
                  data-testid="onboarding-signatory-name"
                  placeholder="Jane Doe"
                  value={form.signatory_name}
                  onChange={(e) => patch("signatory_name", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="onb-signer-title">Title</Label>
                <Input
                  id="onb-signer-title"
                  data-testid="onboarding-signatory-title"
                  placeholder="Head of QA"
                  value={form.signatory_title}
                  onChange={(e) => patch("signatory_title", e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="onb-signer-email">Signatory email</Label>
              <Input
                id="onb-signer-email"
                data-testid="onboarding-signatory-email"
                type="email"
                placeholder="qa@yourdomain.com"
                value={form.signatory_email}
                onChange={(e) => patch("signatory_email", e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-1">
                Signed exports (risk register, trend PDFs, weekly digest) are
                delivered here when you use the &quot;Sign &amp; email&quot; action.
              </p>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3" data-testid="onboarding-step-invite">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="onb-invite-email">Teammate email</Label>
                <Input
                  id="onb-invite-email"
                  data-testid="onboarding-invite-email"
                  type="email"
                  placeholder="ra@yourdomain.com"
                  value={form.invite_email}
                  onChange={(e) => patch("invite_email", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="onb-invite-name">Name</Label>
                <Input
                  id="onb-invite-name"
                  data-testid="onboarding-invite-name"
                  placeholder="Alex"
                  value={form.invite_name}
                  onChange={(e) => patch("invite_name", e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="onb-invite-role">Role</Label>
              <select
                id="onb-invite-role"
                data-testid="onboarding-invite-role"
                value={form.invite_role}
                onChange={(e) => patch("invite_role", e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
              >
                {["QA Lead", "RA", "Clinical", "Manufacturing", "Cybersecurity", "AI Governance", "Auditor"].map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Skip this step to finish alone — you can invite anyone anytime from
                Admin → Invitations.
              </p>
            </div>
          </div>
        )}

        {/* Nav */}
        <div className="flex items-center justify-between pt-4 mt-2 border-t">
          <button
            type="button"
            onClick={() => finish(true)}
            className="text-xs text-slate-500 hover:text-slate-900 underline"
            disabled={saving}
            data-testid="onboarding-skip"
          >
            Skip for now
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={saving}
                data-testid="onboarding-back"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
            {step < total - 1 ? (
              <Button
                size="sm"
                onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
                disabled={!canNext() || saving}
                className="bg-vq-blue text-white hover:bg-vq-blue/90"
                data-testid="onboarding-next"
              >
                Continue
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => finish(false)}
                disabled={saving}
                className="bg-vq-blue text-white hover:bg-vq-blue/90"
                data-testid="onboarding-finish"
              >
                <Check className="h-4 w-4 mr-1" />
                {saving ? "Finishing…" : "Finish"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
